/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import algoliaSearch, { SearchIndex } from "algoliasearch/lite";
import { CodeShareSearch } from "types";
import { runtimeEnv } from "../../configuration";
import { SearchSerializer } from "./SearchSerializer";

export interface SearchOptions {
  filters?: string;
  // facetFilters?: Array<string | string[]>;
  facetFilters?: string | readonly string[] | ReadonlyArray<readonly string[]>;
  page: number;
}

export interface SearchResponse<T> {
  hits: (Hit & T)[];
  page: number;
  nbPages: number;
}

export interface Hit {
  objectID: string;
  _snippetResult: { [id: string]: Result };
  _highlightResult: { [id: string]: Result };
  _rankingInfo: RankingInfo;
}

export interface RankingInfo {
  nbTypos: number;
  firstMatchedWord: number;
  proximityDistance: number;
  userScore: number;
  geoDistance: number;
  geoPrecision: number;
  nbExactWords: number;
  words: number;
  filters: number;
}

export interface Result {
  value: string;
  matchLevel: string;
}

export interface SandboxResult {
  contentType: string;
  tags: string[];
  description: string;
  name: string;
  username: string;
  objectID: string;
}

export interface SearchCodesharesResult {
  codeshares: CodeShareSearch[];
  page: number;
  pages: number;
}

export class SearchClient {
  private static _client: SearchIndex = algoliaSearch(runtimeEnv.REACT_APP_SEARCH_APP_ID, runtimeEnv.REACT_APP_SEARCH_KEY).initIndex(runtimeEnv.REACT_APP_SEARCH_INDEX);

  protected static search = async (query: string, requestOptions?: SearchOptions): Promise<SearchResponse<SandboxResult>> => {
    return SearchClient._client.search(query, requestOptions);
  };

  public static searchCodeshares = async (name: string, page: number = 0): Promise<SearchCodesharesResult> => {
    const searchResult = await SearchClient.search(name, {
      facetFilters: [[
        "contentType:Sandbox",
        "contentType:Sample",
      ]],
      page,
    });
    if (searchResult.hits.length <= 0) {
      throw Error("No results");
    }
    const deserializedResult = (await SearchSerializer.deserialize(searchResult));
    const result: SearchCodesharesResult = {
      codeshares: deserializedResult,
      page: searchResult.page,
      pages: searchResult.nbPages,
    };
    return result;
  };
}
